/*
 * Copyright (C) [2025] [Netzint GmbH]
 * All rights reserved.
 *
 * This software is dual-licensed under the terms of:
 *
 * 1. The GNU Affero General Public License (AGPL-3.0-or-later), as published by the Free Software Foundation.
 *    You may use, modify and distribute this software under the terms of the AGPL, provided that you comply with its conditions.
 *
 *    A copy of the license can be found at: https://www.gnu.org/licenses/agpl-3.0.html
 *
 * OR
 *
 * 2. A commercial license agreement with Netzint GmbH. Licensees holding a valid commercial license from Netzint GmbH
 *    may use this software in accordance with the terms contained in such written agreement, without the obligations imposed by the AGPL.
 *
 * If you are uncertain which license applies to your use case, please contact us at info@netzint.de for clarification.
 */

import { Children, cloneElement, isValidElement, useCallback } from 'react';
import type { HTMLAttributes, MutableRefObject, ReactElement, ReactNode, Ref } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Row } from '@tanstack/react-table';
import cn from '../utils/cn';
import { TableRow } from './Table';
import type { TableRowVariant } from './Table';

export interface DraggableTableRowProps<TData> {
  row: Row<TData>;
  children: ReactNode;
  isRowDisabled?: boolean;
  enableDragAndDrop: boolean;
  canDropOnRow?: (row: TData) => boolean;
  variant?: TableRowVariant;
  isKeyboardFocused?: boolean;
  onRowClick?: (item: TData) => void;
  dragHandleCellIndex?: number;
}

type DragHandleCellProps = HTMLAttributes<HTMLTableCellElement> & {
  ref?: Ref<HTMLTableCellElement>;
};

const assignRef = <TElement,>(ref: Ref<TElement> | undefined, value: TElement | null) => {
  if (!ref) return;

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  const mutableRef = ref as MutableRefObject<TElement | null>;
  mutableRef.current = value;
};

const DraggableTableRow = <TData,>({
  row,
  children,
  isRowDisabled,
  enableDragAndDrop,
  canDropOnRow,
  variant = 'default',
  isKeyboardFocused = false,
  onRowClick,
  dragHandleCellIndex,
}: DraggableTableRowProps<TData>) => {
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    setActivatorNodeRef,
    isDragging,
  } = useDraggable({
    id: row.id,
    data: row.original as Record<string, unknown>,
    disabled: !enableDragAndDrop || isRowDisabled,
  });

  const canDrop = canDropOnRow?.(row.original) ?? false;

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: row.id,
    disabled: !enableDragAndDrop || !canDrop,
    data: row.original as Record<string, unknown>,
  });

  const childrenArray = Children.toArray(children);
  const hasDragHandle =
    typeof dragHandleCellIndex === 'number' &&
    dragHandleCellIndex >= 0 &&
    isValidElement(childrenArray[dragHandleCellIndex]);

  const rowRef = useCallback(
    (element: HTMLTableRowElement | null) => {
      setDragRef(element);
      setDropRef(element);
    },
    [setDragRef, setDropRef],
  );

  const isSelected = row.getIsSelected();

  const handleClick = useCallback(() => {
    onRowClick?.(row.original);
  }, [onRowClick, row.original]);

  const dragHandleChildren = hasDragHandle
    ? childrenArray.map((child, index) => {
        if (index !== dragHandleCellIndex || !isValidElement<DragHandleCellProps>(child)) {
          return child;
        }

        const childElement = child as ReactElement<DragHandleCellProps> & { ref?: Ref<HTMLTableCellElement> };
        const childRef = childElement.ref;

        return cloneElement(childElement, {
          ...listeners,
          ...attributes,
          ref: (element: HTMLTableCellElement | null) => {
            assignRef(childRef, element);
            setActivatorNodeRef(element);
          },
          className: cn(childElement.props.className, enableDragAndDrop && !isRowDisabled && 'cursor-move'),
        });
      })
    : children;

  return (
    <TableRow
      ref={rowRef}
      variant={variant}
      data-row-id={row.id}
      data-state={isSelected ? 'selected' : undefined}
      data-disabled={isRowDisabled ? 'true' : undefined}
      className={cn(
        !hasDragHandle && enableDragAndDrop && !isRowDisabled && 'cursor-move',
        isDragging && 'opacity-30',
        isDragging && isSelected && 'outline outline-2 outline-offset-2 outline-primary',
        isOver && canDrop && 'bg-primary/10 outline outline-2 -outline-offset-2 outline-primary',
        isKeyboardFocused && 'relative z-10 outline outline-2 -outline-offset-2 outline-primary',
      )}
      onClick={handleClick}
      {...(!hasDragHandle ? listeners : {})}
      {...(!hasDragHandle ? attributes : {})}
    >
      {dragHandleChildren}
    </TableRow>
  );
};

export default DraggableTableRow;
